import { useNavigateTo, usePage } from "@tecnobit-srl/router";
import { merge, reduce } from "lodash-es";
import { createContext, useCallback, useContext, useMemo } from "react";
import { jsx } from "react/jsx-runtime";
//#region src/index.tsx
/**
* Turn a Ziggy `uri` into a root-relative URL.
*
* Ziggy reports `uri` without a leading slash (`oauth/logout`, `it/corsi`), so
* interpolating one straight into a template produces a *relative* reference.
* `useNavigateTo` classifies these routes as external — they are not `cms.*` —
* and hands them to `window.location.href` unchanged, so the browser resolves
* them against the current path: from `/it/chi-siamo`, `oauth/logout` becomes
* `/it/oauth/logout`. Only a visitor already at the site root would land on the
* right URL.
*
* Leading slashes are stripped before the prefix is added so this stays correct
* for the hardcoded fallbacks below, which do carry one; otherwise they would
* come out as `//oauth/login`, a protocol-relative URL pointing at another host.
*/
var absoluteUrl = (uri) => `/${uri.replace(/^\/+/, "")}`;
var context = createContext({
	getLoginUrl: () => "",
	login: async () => {
		return Promise.resolve();
	},
	signup: async () => {
		return Promise.resolve();
	},
	logout: async () => {
		return Promise.resolve();
	},
	user: false
});
var { Provider } = context;
var useAuthentication = () => {
	return useContext(context);
};
function AuthenticationProvider({ children }) {
	const page = usePage();
	const navigateTo = useNavigateTo();
	const user = useMemo(() => {
		if (!page.user) return false;
		return reduce(Object.keys(page), (result, key) => {
			if (key.includes("user-")) merge(result, page[key]);
			return result;
		}, { ...page.user });
	}, [page]);
	const previousUrl = page.route.url;
	const loginBaseUrl = useMemo(() => page.ziggy.routes["oauth.login"]?.uri ?? "/oauth/login", [page.ziggy]);
	const getLoginUrl = useCallback((redirectUrl) => `${absoluteUrl(loginBaseUrl)}?${redirectUrl ? `r=${encodeURIComponent(redirectUrl)}&` : ""}p=${encodeURIComponent(previousUrl)}`, [loginBaseUrl, previousUrl]);
	const login = useCallback(async (redirectUrl) => {
		await navigateTo(getLoginUrl(redirectUrl));
	}, [navigateTo, getLoginUrl]);
	const signupUrl = useMemo(() => page.settings.signupPage ?? "/oauth/signup", [page.settings.signupPage]);
	const signup = useCallback(async (redirectUrl) => {
		await navigateTo(`${absoluteUrl(signupUrl)}?${redirectUrl ? `r=${encodeURIComponent(redirectUrl)}&` : ""}p=${encodeURIComponent(previousUrl)}`);
	}, [
		navigateTo,
		signupUrl,
		previousUrl
	]);
	const logoutUrl = useMemo(() => page.ziggy.routes["oauth.logout"]?.uri ?? "/oauth/logout", [page.ziggy]);
	const logout = useCallback(async (redirectUrl) => {
		await navigateTo(`${absoluteUrl(logoutUrl)}?r=${encodeURIComponent(redirectUrl ?? previousUrl)}`);
	}, [
		navigateTo,
		logoutUrl,
		previousUrl
	]);
	const providerValues = useMemo(() => ({
		user,
		getLoginUrl,
		login,
		signup,
		logout
	}), [
		user,
		getLoginUrl,
		login,
		signup,
		logout
	]);
	return /* @__PURE__ */ jsx(Provider, {
		value: providerValues,
		children
	});
}
//#endregion
export { AuthenticationProvider, useAuthentication };
